import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { updateOperatingMode } from "./Api/EndpointApi";
import useUserStore from "../../../services/userStore";
import { Device } from "../interfaces/EndpointInterfaces";
import { myLocalStorage } from "../../StorageHelper";
import ConfirmationDialog from "./ConfirmationDialog";

interface DeviceModeDropdownProps {
  device: Device;
  selectedTenant: any;
  role?: string;
  fetchDeviceList?: () => void;
  disabled?: boolean;
  onModeUpdate?: (deviceId: string) => void;
}


export default function DeviceOperatingMode({
  device,
  fetchDeviceList,
  disabled = false,
  onModeUpdate,
}: DeviceModeDropdownProps) {
  const tenant = myLocalStorage.getItem("latestTenant");
  const [selectedMode, setSelectedMode] = useState(device.mode || "");
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const { email } = useUserStore((state) => state.user);

  useEffect(() => {
    setSelectedMode(device.mode || "");
  }, [device.mode]);

  const handleModeChange = (mode: string) => {
    if (mode === selectedMode) return;
    setPendingMode(mode);
    setShowConfirmDialog(true);
  };

  const handleConfirmChange = async () => {
    if (!pendingMode) return;

    const previousMode = selectedMode;
    const nextMode = pendingMode;

    setIsUpdating(true);
    setSelectedMode(nextMode);
    onModeUpdate?.(device.id);

    try {
      await updateOperatingMode(device.id, nextMode, email);
      fetchDeviceList?.();
    } catch (error) {
      console.error("Failed to update operating mode:", error);
      setSelectedMode(previousMode);
    } finally {
      setIsUpdating(false);
      setPendingMode(null);
    }
  };

  const handleCancelChange = () => {
    setPendingMode(null);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setShowConfirmDialog(open);
    if (!open) {
      setPendingMode(null);
    }
  };


  if (device?.isManuallyAddedMachine) {
    return <span className="text-sm text-gray-500">Not Applicable</span>;
  }



  return (
    <>
      <Select
        value={selectedMode}
        onValueChange={handleModeChange}
        disabled={disabled || isUpdating}
      >
        <SelectTrigger className="h-auto min-h-8 w-44 border-border/70 bg-card px-2 py-1 text-left text-xs whitespace-normal break-words shadow-[0_1px_3px_rgb(15_23_42/0.08)]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="min-w-[16rem]">
          <SelectItem value="AUDITING">AUDITING</SelectItem>
          <SelectItem value="ENFORCING">ENFORCING</SelectItem>
          <SelectItem value="DISABLED">DISABLED</SelectItem>
          <SelectItem value="INHERITED_FROM_PROFILE" className="whitespace-normal">
            INHERITED
            {` (${device.profileMode !== "INHERITED_FROM_TENANT"
              ? device.profileMode
              : tenant?.mode || "N/A"
              })`}
          </SelectItem>
        </SelectContent>
      </Select>

      <ConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={handleDialogOpenChange}
        title="Confirm Operating Mode Change"
        description={`Are you sure you want to change the operating mode to ${pendingMode || "selected mode"}?`}
        confirmText="Update Mode"
        cancelText="Cancel"
        variant="warning"
        loading={isUpdating}
        onConfirm={handleConfirmChange}
        onCancel={handleCancelChange}
      />
    </>
  );
}
